import { AutocompleteCustom } from '@atoms/inputs/input-location';
import { geojsonHelperMixin } from '@elements/geojson-helper';
import { GoogleMapCustom } from '@molecules/google-map';
import { useScreensizeStore } from '@stores/useScreensizeStore.ts';
import { eventBus } from '@tools/event-bus';
import { Logger } from '@tools/logger/logger';
import { getImageUrl } from '@tools/media-image';
import { requestsMixin } from '@tools/requests-mixin';
import { translations } from '@tools/translations/translations';
import { forEach } from 'lodash-es';
import { storeToRefs } from 'pinia';
import { createApp, toRaw } from 'vue';
import { getColorValue } from '../types/colors';

const component = {
  delimiters: [ '${', '}' ],
  mixins: [ geojsonHelperMixin, requestsMixin ],
  setup() {
    const { isMobile } = storeToRefs(useScreensizeStore());

    return {
      isMobile,
    };
  },
  data() {
    return {
      markers: [],
      polygons: [],
      boundingBox: null,
      mapObj: null,
      mapDOMObject: null,
      filterDOMObject: null,
      containerDOMObject: null,
      mapLeftPos: 0,
      maxNbAddresses: 8,
      addressesDisplayed: 2,
      sliderValue: 30,
      noPlaceFound: false,
      searchInProgress: false,
      errorGeojson: false,
      hasOffersInViewport: false,
      form: {
        addresses: [],
        coordinates: [],
        updated: false,
        travelOptions: [],
        selectedTravelOption: [],
      },
      mapDisplayed: false,
    };
  },
  computed: {
    ctaEnabled() {
      return (
        this.form.addresses.filter((item) => item !== '').length > 0
        && this.form.updated
      );
    },
    hasOffers() {
      return this.polygons.length > 0 && this.hasOffersInViewport;
    },
  },
  mounted() {
    this.initDisplay();
    // For dynamically added input addresses
    this.initAutocomplete();
    this.initMap();
    if (window.BREAKPOINTS.isDesktop) {
      this.mapDOMObject = document.querySelector('.ideal-location-map');
      this.filterDOMObject = document.querySelector('.ideal-location-filters');
      this.containerDOMObject = document.querySelector('.ideal-location');
      this.mapLeftPos = this.filterDOMObject.getBoundingClientRect().right;
      this.setStickyMapPosition();
      window.addEventListener('resize', () => {
        this.mapLeftPos = this.filterDOMObject.getBoundingClientRect().right;
        this.setStickyMapPosition();
      });
    }
  },
  watch: {
    sliderValue(value) {
      const percent = (value / 60) * 100;
      this.$refs.timeRange.style.background = `linear-gradient(to right, ${getColorValue('blue')} 0%, ${getColorValue('blue')} ${percent}%, ${getColorValue('gray')} ${percent}%, ${getColorValue('gray')} 100%)`;
    },
  },
  methods: {
    refreshMap() {
      if (this.form.addresses[0] !== '') {
        this.displayAddressOnMap(true);
      }
    },
    /**
     * showMoreAddress
     * @description Add 1 more row to enter address
     */
    showMoreAddress() {
      if (this.addressesDisplayed < this.maxNbAddresses) {
        this.addressesDisplayed += 1;
      }
    },
    /**
     * storeGoogleMapsResults
     * @description Store google autocomplete result in addresses models
     * @param {object} place - data fetched from google api
     * @param {object} inputIndex - the input with autocomplete result
     */
    storeGoogleMapsResults(place, inputIndex) {
      this.form.addresses[inputIndex] = place.name;
      this.form.coordinates[inputIndex] = place;
      this.form.updated = true;
    },
    /**
     * emptyAddress
     * @description Clear input field value with empty string
     * @param {integer} n input number
     */
    emptyAddress(n) {
      this.form.addresses[n] = '';
      this.form.coordinates[n] = {};
      this.form.updated = true;
    },
    /**
     * Display geocoded addresses on map
     * @param refreshFromSlider : If refresh has been triggered from duration
     * slider, we don't want the map to be hidden.
     */
    displayAddressOnMap(refreshFromSlider) {
      if (
        this.isMobile
        && this.mapDisplayed
        && !refreshFromSlider
      ) {
        this.mapDisplayed = false;
        this.form.updated = true;
        return;
      }

      // Remove previously positionned markers & polygons
      this.markers.map((marker) => toRaw(marker).setMap(null));
      this.polygons.map((polygon) => toRaw(polygon).setMap(null));

      this.boundingBox = new google.maps.LatLngBounds(); // eslint-disable-line no-undef
      const origins = [];

      forEach(this.form.coordinates, (place, index) => {
        if (!place.location) {
          return;
        }
        const position = {
          lat: place.location.latitude,
          lng: place.location.longitude,
          id: index + 1,
          tm: {},
        };
        position.tm[this.form.selectedTravelOption[index]] = {};
        origins.push(position);

        // eslint-disable-next-line no-undef
        const marker = new google.maps.Marker({
          position,
          icon: getImageUrl('11040a59-a731-4436-8404-d75a8ebbfc29', 'png'),
          label: {
            color: 'white',
            text: `${index + 1}`,
          },
          map: this.mapObj.map,
        });

        this.markers.push(marker);

        this.boundingBox.extend(position);
      });

      this.hasOffersInViewport = false;
      this.searchInProgress = true;
      this.findIdealLocationPolygons(
        origins,
        this.sliderValue,
        this.displayPolygons,
        this.setNoPlaceFound,
      );

      this.form.updated = false;
      this.mapDisplayed = true;
      this.noPlaceFound = false;

      // Adapt viewport according to all markers
      this.$nextTick(() => {
        if (this.markers.length) {
          window.scrollTo(0, 0);
        }
      });
    },
    /**
     * We display the polygons and save the references to the polygons to delete
     * them in case of a new search
     * @param polygons Array of shape coordinates
     */
    displayPolygons(polygons) {
      const lat = [];
      const lng = [];
      for (let i = 0; i < polygons.length; i++) {
        const formattedArray = this.formatArrayForGmaps(polygons[i]);
        formattedArray.forEach((coord) => {
          lat.push(coord.lat);
          lng.push(coord.lng);
        });

        // eslint-disable-next-line no-undef
        const polygon = new google.maps.Polygon({
          paths: formattedArray,
          strokeColor: getColorValue('blue'),
          strokeOpacity: 1,
          strokeWeight: 2,
          fillColor: getColorValue('blue'),
          fillOpacity: 0.2,
          zIndex: 15,
          clickable: false,
        });
        polygon.setMap(this.mapObj.map);
        this.polygons.push(polygon);
      }
      /* eslint-disable no-undef */
      this.boundingBox.extend(
        new google.maps.LatLng(Math.min(...lat), Math.min(...lng)),
      );
      this.boundingBox.extend(
        new google.maps.LatLng(Math.max(...lat), Math.max(...lng)),
      );
      /* eslint-enable no-undef */

      this.setMapBoundingBox();
      this.searchInProgress = false;
      this.errorGeojson = false;

      // Ask back
      this.checkOffersInArea();
    },
    setMapBoundingBox() {
      this.mapObj.map.fitBounds(this.boundingBox, -50);
    },
    /**
     * Define if places have been found
     * @param  placefound boolean
     * @param error error we might receive from Targomo
     */
    setNoPlaceFound(placefound, error) {
      this.noPlaceFound = placefound;
      this.searchInProgress = false;
      this.errorGeojson = error;
      this.polygons = [];
      this.setMapBoundingBox();
      if (error) {
        Logger.error(error);
      }
    },
    checkOffersInArea() {
      const parameters = {
        north_east_lat: this.mapObj.map.getBounds().getNorthEast().lat(),
        north_east_lon: this.mapObj.map.getBounds().getNorthEast().lng(),
        south_west_lat: this.mapObj.map.getBounds().getSouthWest().lat(),
        south_west_lon: this.mapObj.map.getBounds().getSouthWest().lng(),
      };

      this.post('/search-api/area', parameters)
        .then((response) => response.data)
        .then((json) => {
          this.hasOffersInViewport = json.nbOffers !== undefined && json.nbOffers > 0;
        })
        .catch(() => {
          this.hasOffersInViewport = false;
          Logger.error('error catched');
        });
    },
    goSearch() {
      const travelTime = this.sliderValue;
      const placeIdsList = [];
      const travelModeByPlace = [];
      let teamSize = 0;
      forEach(this.form.coordinates, (item) => {
        if (item.id) {
          placeIdsList.push(item.id);
          travelModeByPlace.push(this.form.selectedTravelOption[teamSize]);
          teamSize++;
        }
      });

      const bounds = this.mapObj.map.getBounds();
      const viewport = {
        north: Math.round(bounds.getNorthEast().lat() * 10000) / 10000,
        east: Math.round(bounds.getNorthEast().lng() * 10000) / 10000,
        south: Math.round(bounds.getSouthWest().lat() * 10000) / 10000,
        west: Math.round(bounds.getSouthWest().lng() * 10000) / 10000,
      };

      const url = `/recherche?sort=default&travel_mode=${travelModeByPlace.join(
        '%2c',
      )}&travel_time=${travelTime}&place_ids=${placeIdsList.join(
        '%2c',
      )}&team-size=${teamSize}&viewport=${Object.values(viewport).join(',')}`; // eslint-disable-line max-len

      window.open(url);
    },
    initAutocomplete() {
      const addresses = document.querySelectorAll('.filters-address__input');
      for (let i = 0; i < addresses.length; i++) {
        const customAutocomplete = new AutocompleteCustom(addresses[i]);
        customAutocomplete.init();
      }
      eventBus.addEventListener('input-location-change', this.handleInputLocationChange);
    },
    handleInputLocationChange(event) {
      const { place, field } = event.detail;
      const inputIndex = field.getAttribute('data-index');
      if (inputIndex != null) {
        this.storeGoogleMapsResults(place, inputIndex);
      }
    },
    initDisplay() {
      this.form.addresses = new Array(this.maxNbAddresses).fill('');
      this.form.coordinates = new Array(this.maxNbAddresses).fill({});
      this.form.selectedTravelOption = new Array(this.maxNbAddresses).fill('transit');

      this.form.travelOptions = [
        {
          name: translations.messages['form.ideal_location.travel_mode.transit'],
          travel_mode: 'transit',
        },
        {
          name: translations.messages['form.ideal_location.travel_mode.walk'],
          travel_mode: 'walk',
        },
        {
          name: translations.messages['form.ideal_location.travel_mode.bike'],
          travel_mode: 'bike',
        },
        {
          name: translations.messages['form.ideal_location.travel_mode.car'],
          travel_mode: 'car',
        },
      ];
    },
    initMap() {
      this.mapObj = new GoogleMapCustom(
        document.querySelector('#gmap-ideal-location'),
        {
          map: {
            center: {
              lat: 48.864716,
              lng: 2.349014,
            },
            zoomPosition: 'top-right',
            fullscreenControl: false,
          },
          circle: {
            show: false,
          },
        },
      );
      this.mapObj.render();
    },
    setStickyMapPosition() {
      this.mapDOMObject.style.position = 'fixed';
      // set left position to right bound of the sticked element.
      this.mapDOMObject.style.left = `${this.mapLeftPos}px`;

      const banner = document.querySelector('#banner');
      const bannerHeight = banner
        ? banner.getBoundingClientRect().height
        : 0;
      const height = document.querySelector('.header').getBoundingClientRect().height
        + bannerHeight;
      const footerHeight = document
        .querySelector('footer.footer')
        .getBoundingClientRect().height;
      this.mapDOMObject.style.top = `${height}px`;
      this.mapDOMObject.style.height = `calc(100vh - ${height + footerHeight}px)`;
      this.mapDOMObject.style.bottom = 'inherit';
      this.mapDOMObject.style.maxWidth = 'none';
      // width for tablet & mobile = wrapper element width - sticked element width
      this.mapDOMObject.style.width = `${
        this.containerDOMObject.clientWidth - this.filterDOMObject.clientWidth
      }px`;

      // width for desktop = wrapper element width - sticked element width + 2 spacing
      if (window.BREAKPOINTS.isDesktop) {
        this.mapDOMObject.style.width = `${
          this.containerDOMObject.clientWidth - this.filterDOMObject.clientWidth
        }px`;
      }
    },
  },
};

export const initIdealLocationMapFilters = () => {
  if (document.querySelector('.ideal-location')) {
    createApp(component).mount('.ideal-location');
  }
};
